import React, {
  Suspense,
  useEffect,
  useState,
  useTransition,
} from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';

import isBot from 'Helpers/SEO/is-bot';
import styles from 'Components/Behaviour/LazyLoading/LazyOnVisible.module.scss';

/**
 * LazyOnVisible
 *
 * This component lazily renders its children only after they enter (or are near) the viewport.
 *
 * It uses:
 * - `react-intersection-observer` to detect visibility.
 * - `useTransition` to defer rendering non-critical content and avoid blocking the main thread.
 * - `<Suspense>` to support React.lazy-loaded children.
 *
 * Why:
 * - Improves performance by deferring below-the-fold or heavy content until needed.
 * - Avoids layout shifts (CLS) by using an invisible 1px trigger instead of rendering placeholders.
 * - Keeps INP fast by deprioritizing rendering via startTransition.
 *
 * Usage:
 * <LazyOnVisible fallback={<Skeleton />}>
 *   <HeavyComponent />
 * </LazyOnVisible>
 * ```
 *
 * - `fallback`: null is safe here — the component only mounts once it’s in view, so there’s no layout shift even if nothing is rendered during loading
 */
export default function LazyOnVisible({
  children,
  fallback = null,
  containerClass = '',
}) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '200px',
  });

  const [shouldRender, setShouldRender] = useState(false);
  const [, startTransition] = useTransition();

  useEffect(() => {
    if (inView && !shouldRender) {
      startTransition(() => {
        setShouldRender(true);
      });
    }
  }, [inView, shouldRender]);


  if (isBot()) {
    return (
      <React.Fragment>
        {children}
      </React.Fragment>
    );
  }

  return (
    <div className={`LazyOnVisible ${containerClass}`}>
      {/* Invisible trigger to avoid CLS */}
      <div ref={ref} style={{ height: 1, width: '100%' }} />

      {/* Defer rendering until inView + transition complete */}
      {shouldRender && (
        <div className={styles.fadeIn}>
          <Suspense fallback={fallback}>
            {children}
          </Suspense>
        </div>
      )}
    </div>
  );
}

LazyOnVisible.propTypes = {
  containerClass: PropTypes.string,
};
